import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "@/pages/layoutChrome";
import FieldHint from "@/components/laboral/FieldHint";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import PortalExternoGuiaPanel from "@/pages/laboral/PortalExternoGuiaPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Save, UserPlus } from "lucide-react";
import { toast } from "sonner";

const CONTEXT_LABEL = "Asistente Laboral y REPSE";
const IDSE_DEFAULT = "https://idse.imss.gob.mx/";

/**
 * Inscripción IMSS: registro patronal obligatorio; obra REPSE opcional.
 */
export default function InscripcionTrabajadoresImss({ embedMode } = {}) {
  const isTramitesEmbed = embedMode === "tramites";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [config, setConfig] = useState(null);
  const [patronales, setPatronales] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [patronId, setPatronId] = useState("");
  const [contratoId, setContratoId] = useState("");
  const [anio, setAnio] = useState(() => new Date().getFullYear());
  const [cuatrimestre, setCuatrimestre] = useState("1");
  const [form, setForm] = useState({
    nss: "",
    sdi: "",
    fecha_alta_imss: "",
    dias_trabajados: "",
  });
  const [busy, setBusy] = useState(false);
  const [loadingWorkers, setLoadingWorkers] = useState(false);

  const selected = useMemo(
    () => workers.find((w) => w.id === selectedId) || null,
    [workers, selectedId],
  );
  const cuatrimestres = config?.cuatrimestres || [];
  const idseUrl = config?.idse_portal_url || IDSE_DEFAULT;

  const loadWorkers = useCallback(async () => {
    if (!clientId) {
      setWorkers([]);
      return;
    }
    setLoadingWorkers(true);
    try {
      const { data } = await api.get("/laboral/trabajadores-registro", { params: { client_id: clientId } });
      setWorkers(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setWorkers([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar trabajadores");
    } finally {
      setLoadingWorkers(false);
    }
  }, [clientId]);

  const loadPatronales = useCallback(async () => {
    if (!clientId) { setPatronales([]); return; }
    try {
      const { data } = await api.get("/laboral/registros-patronales", { params: { client_id: clientId } });
      setPatronales(Array.isArray(data.items) ? data.items : []);
    } catch { setPatronales([]); }
  }, [clientId]);

  const loadContratos = useCallback(async () => {
    if (!clientId) {
      setContratos([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/contratos-repse", { params: { client_id: clientId } });
      setContratos(Array.isArray(data.items) ? data.items : []);
    } catch {
      setContratos([]);
    }
  }, [clientId]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setConfig(null);
      return;
    }
    api
      .get("/laboral/inscripcion-imss/config", { params: { client_id: clientId } })
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig(null));
    loadPatronales();
    loadContratos();
    loadWorkers();
    setSelectedId("");
    setContratoId("");
  }, [clientId, loadPatronales, loadContratos, loadWorkers]);

  useEffect(() => {
    if (!selected) {
      setForm({ nss: "", sdi: "", fecha_alta_imss: "", dias_trabajados: "" });
      setPatronId("");
      setContratoId("");
      return;
    }
    const clave = `${anio}-C${cuatrimestre}`;
    const hist = selected.historial_cuatrimestral?.[clave] || {};
    setPatronId(selected.registro_patronal_id || "");
    setContratoId(selected.id_contrato_asignado || "");
    setForm({
      nss: selected.nss || "",
      sdi: hist.sdi || selected.sdi || "",
      fecha_alta_imss: selected.fecha_alta_imss || "",
      dias_trabajados: hist.dias_trabajados || selected.dias_trabajados_periodo || "",
    });
  }, [selected, anio, cuatrimestre]);

  const onSave = async () => {
    if (!clientId || !selectedId) {
      toast.error("Seleccione un trabajador");
      return;
    }
    if (!patronId) {
      toast.error("Seleccione el registro patronal");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/laboral/trabajadores-registro/${selectedId}/obra-imss`, {
        client_id: clientId,
        registro_patronal_id: patronId,
        contrato_id: contratoId || "",
        nss: form.nss,
        sdi: form.sdi,
        fecha_alta_imss: form.fecha_alta_imss,
        anio: Number(anio),
        cuatrimestre,
        dias_trabajados: form.dias_trabajados,
      });
      toast.success("Datos IMSS guardados en catálogo");
      await loadWorkers();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  const panelBody = (
      <div className="space-y-6">
      {!clientId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Seleccione un cliente de trabajo en el Panel General.
        </div>
      )}

      <PortalExternoGuiaPanel
        title="Portal IDSE — alta o movimientos IMSS"
        intro={config?.instrucciones || ""}
        portalUrl={idseUrl}
        steps={[
          "Inicie sesión en IDSE con el registro patronal y certificado configurados.",
          "Registre el alta del trabajador o confirme movimientos afiliatorios.",
          "Capture aquí el NSS asignado, SDI y fecha de alta para la obra seleccionada.",
        ]}
      />

      <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Trabajadores del cliente
        </h3>
        {loadingWorkers ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando trabajadores…
          </div>
        ) : workers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No hay trabajadores registrados. Use Inscripción SAT o importación previa.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>RFC / CURP</TableHead>
                  <TableHead>NSS</TableHead>
                  <TableHead>Obra asignada</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {workers.map((w) => {
                  const obra = contratos.find((c) => c.id === w.id_contrato_asignado);
                  return (
                    <TableRow key={w.id} data-selected={w.id === selectedId}>
                      <TableCell>{w.nombre_completo || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{w.rfc || w.curp || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{w.nss || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {obra ? `${obra.numero_contrato} — ${obra.razon_social_obra || "Obra"}` : "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedId === w.id ? "default" : "outline"}
                          onClick={() => setSelectedId(w.id)}
                        >
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {selectedId ? (
        <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
          <h3 className="text-sm font-medium">Alta / datos IMSS — {selected?.nombre_completo}</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <Label>Registro patronal</Label>
              <FieldHint>Obligatorio: matriz u obra especializada IMSS</FieldHint>
              <Select value={patronId} onValueChange={setPatronId} disabled={!clientId || busy}>
                <SelectTrigger><SelectValue placeholder="Seleccione patrón" /></SelectTrigger>
                <SelectContent>
                  {patronales.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.registro_patronal} — {p.nombre_sucursal_obra || p.razon_social_imss || "Patrón"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Obra / contrato REPSE (opcional)</Label>
              <FieldHint>Solo si aplica subcontratación REPSE</FieldHint>
              <Select value={contratoId} onValueChange={setContratoId} disabled={!clientId || busy}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione contrato" />
                </SelectTrigger>
                <SelectContent>
                  {contratos.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.numero_contrato} — {c.razon_social_obra || "Obra"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="imss-nss">NSS</Label>
              <FieldHint>11 dígitos asignados por IMSS tras el alta en IDSE</FieldHint>
              <Input
                id="imss-nss"
                value={form.nss}
                onChange={(e) => setForm((p) => ({ ...p, nss: e.target.value }))}
                disabled={busy}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="imss-sdi">SDI</Label>
              <FieldHint>Salario diario integrado para el cuatrimestre seleccionado</FieldHint>
              <Input
                id="imss-sdi"
                value={form.sdi}
                onChange={(e) => setForm((p) => ({ ...p, sdi: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="imss-fecha">Fecha alta IMSS</Label>
              <FieldHint>Fecha efectiva del alta en IDSE (AAAA-MM-DD)</FieldHint>
              <Input
                id="imss-fecha"
                type="date"
                value={form.fecha_alta_imss}
                onChange={(e) => setForm((p) => ({ ...p, fecha_alta_imss: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-2">
              <Label>Año cuatrimestre</Label>
              <FieldHint>Periodo al que aplican SDI y días capturados</FieldHint>
              <Input
                type="number"
                min={2000}
                max={2100}
                value={anio}
                onChange={(e) => setAnio(Number(e.target.value))}
                disabled={busy}
              />
            </div>
            <div className="space-y-2">
              <Label>Cuatrimestre</Label>
              <Select value={cuatrimestre} onValueChange={setCuatrimestre} disabled={busy}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(cuatrimestres.length ? cuatrimestres : [
                    { id: "1", label: "1er cuatrimestre" },
                    { id: "2", label: "2do cuatrimestre" },
                    { id: "3", label: "3er cuatrimestre" },
                  ]).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="imss-dias">Días trabajados (periodo)</Label>
              <FieldHint>Días del cuatrimestre para SISUB/ICSOE</FieldHint>
              <Input
                id="imss-dias"
                value={form.dias_trabajados}
                onChange={(e) => setForm((p) => ({ ...p, dias_trabajados: e.target.value }))}
                disabled={busy}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={onSave} disabled={busy || !patronId}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar datos IMSS
            </Button>
          </div>
        </section>
      ) : null}
      </div>
  );

  return (
    <div className="space-y-6" data-testid="inscripcion-trabajadores-imss-page">
      {!isTramitesEmbed ? (
        <PageHeader
          title="Inscripción a Trabajadores IMSS"
          subtitle={CONTEXT_LABEL}
          description="Paso 4: patrón obligatorio; NSS, SDI y fecha de alta. Obra REPSE opcional hasta subcontratación."
        />
      ) : null}
      {isTramitesEmbed ? panelBody : <LaboralFlujoOperativo>{panelBody}</LaboralFlujoOperativo>}
    </div>
  );
}
